import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { css } from '@emotion/react';

import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import NvIcon from 'shared/components/nv-icon';
import { useAppDispatch } from 'redux/store';
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { toggleAiFeedback } from 'redux/actions/submissions';
import { InsightVisibility } from 'redux/schemas/models/exercise';
import { NvTooltip, TextAlign } from 'shared/components/nv-tooltip';
import { semiBoldFontWeight } from 'styles/global_defaults/fonts';
import AiInsightsSetupModal from './ai-insights-setup-modal';

interface AiInsightsDropdownItemProps {
  exerciseId: string;
  lectureComponentId: string;
  isCollection?: boolean;
}

const styles = css`
  .font-semibold {
    font-weight: ${semiBoldFontWeight};
  }
`;

const getVisibilityLabel = (visibility: InsightVisibility, courseAliases: Record<string, string>) => {
  const translations = t.LECTURE_PAGES.COMPONENTS.AI_INSIGHTS;
  switch (visibility) {
    case InsightVisibility.LEARNERS:
      return translations.SETUP_DIRECT_TO_LEARNER(courseAliases);
    case InsightVisibility.ADMINS:
      return translations.SETUP_ADMINS_ONLY();
    case InsightVisibility.DISABLED:
    default:
      return translations.SETUP_DISABLED();
  }
};

const AiInsightsDropdownItem = ({ exerciseId, lectureComponentId, isCollection }: AiInsightsDropdownItemProps) => {
  const [showModal, setShowModal] = useState(false);
  const [currentVisibility, setCurrentVisibility] = useState<InsightVisibility>(InsightVisibility.DISABLED);
  const dispatch = useAppDispatch();

  const currentCourse = useSelector(getCurrentCourse);
  const courseAliases = useSelector(state => getFlatCourseAliases(state, currentCourse?.catalogId));
  const exercises = useSelector(state => state?.app?.lecturePage?.exercises);
  const currentLecturePageComponents = useSelector(state => state?.app?.lecturePage?.currentLecturePageComponents);

  const exerciseFromList = exercises?.find(ex => ex?.id === exerciseId);
  const exerciseFromComponents = currentLecturePageComponents?.find(comp => comp?.exercise?.id === exerciseId)?.exercise;
  const currentExercise = exerciseFromList ?? exerciseFromComponents;

  useEffect(() => {
    if (currentExercise) {
      setCurrentVisibility(currentExercise.insightVisibility ?? (currentExercise.aiFeedbackEnabled ? InsightVisibility.ADMINS : InsightVisibility.DISABLED));
    }
  }, [currentExercise]);

  const handleClick = () => {
    if (isCollection) return;
    // Dismiss the NvDropdown
    document.getElementById(`lecture-component-${lectureComponentId}`)?.click();
    setShowModal(true);
  };

  const handleSave = (insightVisibility: InsightVisibility) => {
    setCurrentVisibility(insightVisibility);
    setShowModal(false);
    dispatch(toggleAiFeedback({
      catalogId: currentCourse.catalogId,
      exerciseId,
      aiFeedbackEnabled: insightVisibility !== InsightVisibility.DISABLED,
      insightVisibility,
    }));
  };

  return (
    <>
      <NvTooltip
        text={t.LECTURE_PAGES.COMPONENTS.DROPDOWN.SETUP_IN_LINKED_LESSON_TOOLTIP()}
        placement='left'
        enabled={isCollection}
        textAlign={TextAlign.LEFT}
      >
        <div
          css={styles}
          className={`bs4-dropdown-item d-flex pl-2 align-items-start ${isCollection ? 'text-gray-5' : ''}`}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          tabIndex={0}
          role='menuitem'
          data-qa='ai-insights-setup-dropdown-item'
        >
          <div className='d-flex flex-column pr-2'>
            <div className='d-flex'>
              {currentVisibility === InsightVisibility.DISABLED
                ? <NvIcon size='xss-smallest' icon='gen-ai' className='pr-1 mt-1' />
                : <GradientAiIcon size='xss-smallest' icon='gen-ai' className='pr-1 mt-1' />}
              <span>{t.LECTURE_PAGES.COMPONENTS.AI_INSIGHTS.SETUP()}</span>
            </div>
            <span className='text-small text-gray-3 font-semibold'>
              {getVisibilityLabel(currentVisibility, courseAliases)}
            </span>
          </div>
        </div>
      </NvTooltip>
      <AiInsightsSetupModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        currentVisibility={currentVisibility}
        courseAliases={courseAliases}
      />
    </>
  );
};

export default AiInsightsDropdownItem;
